import React, { useState, useEffect, ComponentType } from 'react';
import { Content, Progress, SignInPage } from '@backstage/core-components';
import {
  Button,
  Dialog,
  Grid,
  AppBar,
  Toolbar,
  Typography,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Slide,
  CircularProgress,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import { useForm, Controller } from 'react-hook-form';
import { Alert } from '@material-ui/lab';
import {
  useApi,
  SignInPageProps,
  configApiRef,
  identityApiRef,
  BackstageIdentityResponse,
} from '@backstage/core-plugin-api';
import { SignInProviderConfig } from './types';
import { useServiceRegistryTranslation } from '../i18n/ServiceRegistryLanguageProvider';

// Slide transition for Dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type SingleSignInPageProps = SignInPageProps & {
  provider: SignInProviderConfig;
  auto?: boolean;
  ErrorComponent?: ComponentType<{ error?: Error }>;
};

const CustomSignInPage = ({
  provider,
  auto,
  onSignInSuccess,
  ErrorComponent,
}: SingleSignInPageProps) => {
  const { t } = useServiceRegistryTranslation();
  const config = useApi(configApiRef);
  const authApi = useApi(provider.apiRef);
  const baseUrl = config.getString('backend.baseUrl');
  const key = config.getString('backend.baseUrl');
  const [isLoading, setIsLoading] = useState(true);
  const [showSignIn, setShowSignIn] = useState(false);
  const [signInKey, setSignInKey] = useState(Date.now());
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  // Add loading state for registration
  const [isRegistering, setIsRegistering] = useState(false);

  const identityApi = useApi(identityApiRef);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
    },
  });

  useEffect(() => {
    const checkSession = async () => {
      try {
        const identityResponse = await authApi.getBackstageIdentity({
          optional: true,
        });
        if (identityResponse) {
          setShowSignIn(true);
        }
      } catch (error) {
        console.error('Session check failed:', error);
      } finally {
        setIsLoading(false);
      }
    };
    checkSession();
  }, []);

  const handleOpenRegisterModal = () => {
    setShowRegisterModal(true);
    setRegistrationError('');
    setRegistrationSuccess(false);
    setIsRegistering(false); // Reset loading state
  };

  const handleCloseRegisterModal = () => {
    setShowRegisterModal(false);
    setIsRegistering(false); // Reset loading state
    reset();
  };

  const handleSignIn = () => {
    setSignInKey(Date.now()); // force fresh mount
    setShowSignIn(true);
  };

  const handleCloseSignIn = () => {
    setShowSignIn(false);
  };

  const onSubmit = async (data: any) => {
    setIsRegistering(true); // Start loading
    setRegistrationError(''); // Clear previous errors
    
    try {
      const encodedSecret = btoa(key);
      const response = await fetch(`${baseUrl}/api/users/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${encodedSecret}`,
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error('REGISTRATION_FAILED');
      }

      setRegistrationSuccess(true);
      setRegistrationError(t('signIn.registrationSuccess'));

      setTimeout(() => {
        handleCloseRegisterModal();
        handleSignIn();
      }, 3000);
    } catch (error: any) {
      console.error('Registration error:', error);
      setRegistrationError(t('signIn.registrationFailed'));
    } finally {
      setIsRegistering(false); // Stop loading
    }
  };

  if (isLoading) {
    return <Progress />;
  }

  return (
    <>
      <AppBar
        position="static"
        style={{
          background: 'linear-gradient(90deg, #00529c, #00a6dc)',
          height: '100px',
          justifyContent: 'center',
        }}
      >
        <Toolbar>
          <img
            src="/logo.png"
            alt={t('signIn.logoAlt')}
            style={{ width: '80px', height: 'auto', marginRight: '20px' }}
          />
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            {t('signIn.applicationName')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSignIn}
            style={{ marginRight: '10px' }}
          >
            {t('signIn.signIn')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleOpenRegisterModal}
          >
            {t('signIn.register')}
          </Button>
        </Toolbar>
      </AppBar>

      <Content>
        <Grid container spacing={2} direction="column" style={{ padding: '20px' }}>
          <Grid item>
            <Typography variant="h5">{t('signIn.welcome')}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1">
              {t('signIn.instructions')}
            </Typography>
          </Grid>
        </Grid>
      </Content>

      {/* Registration Modal */}
      <Dialog open={showRegisterModal} onClose={handleCloseRegisterModal}>
        <DialogTitle>{t('signIn.register')}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            {registrationError && (
              <Alert
                severity={registrationSuccess ? 'success' : 'error'}
                style={{ marginBottom: '1rem' }}
              >
                {registrationError}
              </Alert>
            )}
            <Controller
              name="first_name"
              control={control}
              rules={{ 
                required: t('signIn.required', { field: t('signIn.firstName') }),
                minLength: {
                  value: 2,
                  message: t('signIn.firstNameMin'),
                },
                maxLength: {
                  value: 15,
                  message: t('signIn.firstNameMax'),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  margin="dense"
                  label={t('signIn.firstName')}
                  type="text"
                  fullWidth
                  disabled={isRegistering} // Disable during loading
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
              rules={{ 
                required: t('signIn.required', { field: t('signIn.lastName') }),
                minLength: {
                  value: 2,
                  message: t('signIn.lastNameMin'),
                },
                maxLength: {
                  value: 15,
                  message: t('signIn.lastNameMax'),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="dense"
                  label={t('signIn.lastName')}
                  type="text"
                  fullWidth
                  disabled={isRegistering} // Disable during loading
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{
                required: t('signIn.required', { field: t('signIn.email') }),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('signIn.invalidEmail'),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="dense"
                  label={t('signIn.email')}
                  type="email"
                  fullWidth
                  disabled={isRegistering} // Disable during loading
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseRegisterModal} 
              color="primary"
              disabled={isRegistering} // Disable during loading
            >
              {t('signIn.cancel')}
            </Button>
            <Button 
              type="submit" 
              color="primary" 
              disabled={registrationSuccess || isRegistering} // Disable during loading or after success
              startIcon={isRegistering ? <CircularProgress size={20} /> : null}
            >
              {isRegistering ? t('signIn.registering') : t('signIn.register')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Sign In Dialog with Reset Support */}
      <Dialog
        open={showSignIn}
        onClose={handleCloseSignIn}
        TransitionComponent={Transition}
        keepMounted={false}
        style={{ display: 'none' }}
      >
        <SignInPage key={signInKey} provider={provider} auto={auto} onSignInSuccess={onSignInSuccess} ErrorComponent={ErrorComponent} />
      </Dialog>
    </>
  );
};

export default CustomSignInPage;
